import React, { useState } from 'react';
import { ArrowUpRight, X, Check, Loader2, Play } from 'lucide-react';
import { footerData } from '../../data/mock';
import axios from 'axios';
import DemoModal from './DemoModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isEnquiryModalOpen, setIsEnquiryModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      await axios.post(`${API}/enquiry`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: formData.message
      });
      
      setIsSubmitting(false);
      setIsSuccess(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSuccess(false);
        setIsEnquiryModalOpen(false);
        setFormData({ name: '', email: '', phone: '', message: '' });
      }, 3000);
    } catch (err) {
      setIsSubmitting(false);
      setError('Failed to submit enquiry. Please try again or email us directly.');
    }
  };

  return (
    <>
      <footer id="contact" className="relative bg-black overflow-hidden">
        {/* Ambient Light */}
        <div className="absolute top-0 right-1/4 w-[400px] md:w-[800px] h-[200px] md:h-[400px] bg-gradient-radial from-amber-900/10 via-transparent to-transparent blur-3xl" />
        
        {/* CTA Section */}
        <div className="relative z-10 max-w-[1600px] mx-auto px-4 md:px-8 lg:px-16 py-16 md:py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-end">
            <div>
              <p className="text-white/30 text-[10px] md:text-xs font-medium tracking-[0.2em] md:tracking-[0.3em] mb-3 md:mb-6">
                [ GET IN TOUCH ]
              </p>
              <h2 className="text-2xl md:text-3xl lg:text-5xl font-light text-white leading-tight tracking-tight mb-4 md:mb-6">
                Get in touch for a <span className="text-amber-400">live demo</span> and know more
              </h2>
              <p className="text-white/40 text-xs md:text-base font-light max-w-lg leading-relaxed mb-3 md:mb-4">
                Connect with us to see our platform in action and learn how we can help manage your digital assets.
              </p>
              <a 
                href={`mailto:${footerData.email}`}
                className="text-amber-500/80 hover:text-amber-400 text-xs md:text-sm font-light transition-colors duration-300"
              >
                {footerData.email}
              </a>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 lg:justify-end">
              <button 
                onClick={() => setIsDemoModalOpen(true)}
                className="group px-6 md:px-8 py-3 md:py-3.5 text-[10px] md:text-xs font-medium tracking-[0.15em] md:tracking-[0.2em] bg-amber-500 text-black hover:bg-amber-400 transition-all duration-500 rounded-sm flex items-center justify-center gap-2 md:gap-3"
              >
                <Play className="w-3 h-3 md:w-4 md:h-4" />
                REQUEST LIVE DEMO
              </button>
              <button 
                onClick={() => setIsEnquiryModalOpen(true)}
                className="px-6 md:px-8 py-3 md:py-3.5 text-[10px] md:text-xs font-medium tracking-[0.15em] md:tracking-[0.2em] border border-white/20 text-white/80 hover:border-white/40 transition-all duration-500 rounded-sm flex items-center justify-center gap-2"
              >
                ENQUIRE NOW
                <ArrowUpRight className="w-3 h-3 md:w-4 md:h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="border-t border-white/[0.06]">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-16 py-4 md:py-8">
            <p className="text-white/25 text-[9px] md:text-xs font-light leading-relaxed max-w-5xl">
              <span className="text-white/40 font-medium">Disclaimer:</span> {footerData.disclaimer}
            </p>
          </div>
        </div>

        {/* HQ Address */}
        <div className="border-t border-white/[0.06]">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-16 py-3 md:py-4">
            <div className="flex items-center gap-2">
              <svg className="w-3 h-3 md:w-3.5 md:h-3.5 text-white/30 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p className="text-white/25 text-[9px] md:text-[10px] font-light">
                <span className="text-white/35">HQ:</span> Meydan Grandstand, 6th Floor, Meydan Road, Dubai, UAE
              </p>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="border-t border-white/[0.06]">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-16 py-4 md:py-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 md:gap-4">
              {/* Logo & Copyright */}
              <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6">
                <div className="flex items-center gap-2">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_coin-control-1/artifacts/2m2ji0tz_85e9384d-89d9-4ef4-b04a-b727bf317f00.jpg" 
                    alt="Coin Bills Logo" 
                    className="h-5 w-5 object-contain opacity-80"
                  />
                  <span className="brand-font text-white/50 text-[10px] md:text-xs">COIN BILLS</span>
                </div>
                <span className="text-white/20 text-[9px] md:text-xs font-light">
                  © {currentYear} Coin Bills. All rights reserved.
                </span>
              </div>

              {/* Links */}
              <div className="flex items-center gap-4 md:gap-6">
                <a href="#" className="text-white/25 hover:text-white/50 text-[9px] md:text-xs font-light tracking-wide transition-colors duration-300">
                  Privacy
                </a>
                <a href="#" className="text-white/25 hover:text-white/50 text-[9px] md:text-xs font-light tracking-wide transition-colors duration-300">
                  Terms
                </a>
                <a 
                  href={`mailto:${footerData.email}`}
                  className="text-white/25 hover:text-white/50 text-[9px] md:text-xs font-light tracking-wide transition-colors duration-300"
                >
                  {footerData.email}
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Enquiry Modal */}
      {isEnquiryModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => !isSubmitting && setIsEnquiryModalOpen(false)}
          />
          
          {/* Modal */}
          <div className="relative w-full max-w-lg bg-[#0a0a0a] border border-white/10 rounded-lg overflow-hidden animate-fade-in-up">
            {/* Close Button */}
            <button 
              onClick={() => !isSubmitting && setIsEnquiryModalOpen(false)}
              className="absolute top-3 right-3 md:top-4 md:right-4 text-white/40 hover:text-white transition-colors z-10"
              disabled={isSubmitting}
            >
              <X className="w-4 h-4 md:w-5 md:h-5" />
            </button>

            {isSuccess ? (
              /* Success State */
              <div className="p-8 md:p-10 text-center">
                <div className="w-14 h-14 md:w-16 md:h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                  <Check className="w-7 h-7 md:w-8 md:h-8 text-amber-500" />
                </div>
                <h3 className="text-xl md:text-2xl font-light text-white mb-2 md:mb-3">Thank You!</h3>
                <p className="text-white/50 text-xs md:text-sm font-light">
                  Your enquiry has been submitted successfully.<br />
                  We will get back to you shortly.
                </p>
              </div>
            ) : (
              /* Form State */
              <form onSubmit={handleSubmit} className="p-5 md:p-8">
                <h3 className="text-lg md:text-2xl font-light text-white mb-1 md:mb-2">Get in Touch</h3>
                <p className="text-white/40 text-[10px] md:text-sm font-light mb-5 md:mb-8">
                  Request a live demo or ask us anything
                </p>
                
                <div className="space-y-4 md:space-y-5">
                  {/* Name */}
                  <div>
                    <label className="block text-white/50 text-[9px] md:text-xs font-medium tracking-wider mb-1.5 md:mb-2">
                      FULL NAME *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-white/[0.03] border border-white/10 rounded px-3 md:px-4 py-2.5 md:py-3 text-white text-xs md:text-sm font-light placeholder-white/30 focus:outline-none focus:border-amber-500/50 transition-colors"
                      placeholder="Enter your full name"
                    />
                  </div>
                  
                  {/* Email */}
                  <div>
                    <label className="block text-white/50 text-[9px] md:text-xs font-medium tracking-wider mb-1.5 md:mb-2">
                      EMAIL ADDRESS *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-white/[0.03] border border-white/10 rounded px-3 md:px-4 py-2.5 md:py-3 text-white text-xs md:text-sm font-light placeholder-white/30 focus:outline-none focus:border-amber-500/50 transition-colors"
                      placeholder="Enter your email address"
                    />
                  </div>
                  
                  {/* Phone */}
                  <div>
                    <label className="block text-white/50 text-[9px] md:text-xs font-medium tracking-wider mb-1.5 md:mb-2">
                      PHONE NUMBER
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full bg-white/[0.03] border border-white/10 rounded px-3 md:px-4 py-2.5 md:py-3 text-white text-xs md:text-sm font-light placeholder-white/30 focus:outline-none focus:border-amber-500/50 transition-colors"
                      placeholder="Enter your phone number"
                    />
                  </div>
                  
                  {/* Message */}
                  <div>
                    <label className="block text-white/50 text-[9px] md:text-xs font-medium tracking-wider mb-1.5 md:mb-2">
                      MESSAGE *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={3}
                      className="w-full bg-white/[0.03] border border-white/10 rounded px-3 md:px-4 py-2.5 md:py-3 text-white text-xs md:text-sm font-light placeholder-white/30 focus:outline-none focus:border-amber-500/50 transition-colors resize-none"
                      placeholder="I'd like to request a live demo..."
                    />
                  </div>
                </div>
                
                {/* Submit Button */}
                {error && (
                  <p className="text-red-400 text-[10px] md:text-xs font-light mt-3 md:mt-4">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full mt-5 md:mt-6 px-6 md:px-8 py-3 md:py-4 text-[10px] md:text-xs font-medium tracking-[0.15em] md:tracking-[0.2em] bg-amber-500 text-black hover:bg-amber-400 transition-all duration-500 rounded-sm flex items-center justify-center gap-2 md:gap-3 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-3 h-3 md:w-4 md:h-4 animate-spin" />
                      SUBMITTING...
                    </>
                  ) : (
                    <>
                      SUBMIT ENQUIRY
                      <ArrowUpRight className="w-3 h-3 md:w-4 md:h-4" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Interactive Demo Modal */}
      <DemoModal 
        isOpen={isDemoModalOpen} 
        onClose={() => setIsDemoModalOpen(false)} 
      />
    </>
  );
};

export default Footer;
